/**
 * VideoLibrary.jsx — Learning Paths Video Library admin UI (P1)
 *
 * Per spec /app/specs/LEARNING_PATHS_VIDEO_LIBRARY_SPEC.md.
 *
 * Single self-contained component file for P1 — splits into individual
 * components when the panel grows past ~600 LOC (P2/P3).
 *
 * Exports:
 *   - VideoLibraryPanel (default)  — top-level Manage Videos surface
 *   - VideoPlayerStrip             — chapter-reader iframe player block
 *
 * Gated by capability `videos_admin` (super_admin + crm_admin defaults).
 */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import VideoCustomControls, { isCustomControlsEnabled } from "@/components/VideoCustomControls";
import {
  Plus, Search, Trash2, Edit2, Youtube, Film, X, Save,
  Loader2, Tag, ListChecks, Layers, ChevronRight, GripVertical,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────
 *  Add Video form (single-URL)
 * ─────────────────────────────────────────────────────────────────── */
function AddVideoForm({ onSaved, onCancel, token }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState("");
  const [tags, setTags] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  // Live URL preview — debounced 400ms.  Also auto-fills the title field
  // from oEmbed when the admin hasn't typed one yet (so they don't have
  // to retype the YouTube title).
  useEffect(() => {
    if (!url || url.length < 10) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/learning-videos/videos/bulk-preview`,
          { urls: [url] },
          { headers: { Authorization: `Bearer ${token}` } });
        const p = res.data?.previews?.[0];
        setPreview(p?.ok ? p : null);
        if (p?.ok && p.title_suggestion && !title.trim()) {
          setTitle(p.title_suggestion);
        }
      } catch { setPreview(null); }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally
    // ignoring `title` so the auto-fill doesn't re-fire when the user
    // edits the title afterwards (we only seed on first preview).
  }, [url, token]);

  const submit = async () => {
    if (!url.trim() || !title.trim() || !duration.trim()) {
      toast.error("URL, title, and duration are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/learning-videos/videos`, {
        url, title, duration, description,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Video added to library");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save video");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 mb-4" data-testid="add-video-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-[#1B365D]">Add video to library</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-700" data-testid="add-video-cancel"><X className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-8">
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Video URL</label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=… or https://vimeo.com/…"
                 data-testid="add-video-url" />
          {preview && (
            <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
              {preview.thumbnail_url && <img src={preview.thumbnail_url} alt="" className="h-10 w-16 rounded object-cover" />}
              <div>
                <Badge variant="secondary" className="mr-1">{preview.platform}</Badge>
                ID: <span className="font-mono">{preview.video_id}</span>
              </div>
            </div>
          )}
        </div>
        <div className="col-span-4">
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Duration <span className="text-rose-500">*</span></label>
          <Input value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="mm:ss" data-testid="add-video-duration" />
        </div>
        <div className="col-span-8">
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Title <span className="text-rose-500">*</span></label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Part 1 — Opening & Problem Statement"
                 data-testid="add-video-title" />
        </div>
        <div className="col-span-4">
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Tags (comma-sep)</label>
          <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="EMR, Scripts" data-testid="add-video-tags" />
        </div>
        <div className="col-span-12">
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Description</label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="One-line description shown in the chapter editor"
                 data-testid="add-video-description" />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-4">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-[#0071E3] hover:bg-[#0062C9] text-white" data-testid="add-video-submit">
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          Save to library
        </Button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 *  Bulk Paste form
 * ─────────────────────────────────────────────────────────────────── */
function BulkPasteForm({ onSaved, onCancel, token }) {
  const [pasted, setPasted] = useState("");
  const [previews, setPreviews] = useState(null);
  const [playlistNotices, setPlaylistNotices] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);

  const runPreview = async () => {
    const urls = pasted.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (!urls.length) { toast.error("Paste at least one URL"); return; }
    if (urls.length > 50) { toast.error("Max 50 URLs per request"); return; }
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/learning-videos/videos/bulk-preview`,
        { urls }, { headers: { Authorization: `Bearer ${token}` } });
      const ps = res.data.previews;
      const notices = res.data.playlist_notices || [];
      setPreviews(ps);
      setPlaylistNotices(notices);
      // Auto-fill titles from oEmbed.  Admin can still edit any row's
      // title before save — we just give them a head-start instead of
      // an empty input on every row.
      setRows(ps.filter((p) => p.ok).map((p) => ({
        url: p.url, title: p.title_suggestion || "", duration: "", tags: "", description: "",
        platform: p.platform, thumbnail_url: p.thumbnail_url,
      })));
      const expanded = notices.reduce((acc, n) => acc + (n.expanded_count || 0), 0);
      if (expanded) toast.success(`Expanded ${expanded} videos from ${notices.length} playlist${notices.length === 1 ? "" : "s"} · auto-pulled titles`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const saveAll = async () => {
    const bad = rows.find((r) => !r.title.trim() || !r.duration.trim());
    if (bad) { toast.error("Every row needs a title and duration"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/learning-videos/videos/bulk-save`, {
        rows: rows.map((r) => ({
          url: r.url, title: r.title, duration: r.duration,
          description: r.description,
          tags: r.tags.split(",").map((t) => t.trim()).filter(Boolean),
        })),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.data.count} videos saved · ${res.data.errors.length} errors`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk save failed");
    } finally { setSaving(false); }
  };

  const updateRow = (idx, key, val) => {
    setRows((rs) => rs.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  };

  const invalidPreviews = previews?.filter((p) => !p.ok) || [];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 mb-4" data-testid="bulk-paste-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-[#1B365D]">Bulk paste — up to 50 URLs</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
      </div>
      {!previews && (
        <>
          <textarea value={pasted} onChange={(e) => setPasted(e.target.value)}
            placeholder="Paste YouTube/Vimeo URLs (one per line) or a YouTube playlist URL…"
            className="w-full min-h-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
            data-testid="bulk-paste-textarea" />
          <div className="text-[11px] text-slate-500 mt-2 flex items-start gap-1.5">
            <Layers className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Tip:</strong> Paste a YouTube playlist URL like
              <code className="font-mono text-[10px] bg-slate-100 rounded px-1 mx-1">youtube.com/playlist?list=…</code>
              to expand all its videos at once. Titles auto-fill from YouTube — you'll just need to fill in the duration.
            </span>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button onClick={runPreview} disabled={previewing} className="bg-[#0071E3] hover:bg-[#0062C9] text-white" data-testid="bulk-paste-preview">
              {previewing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Preview
            </Button>
          </div>
        </>
      )}
      {previews && (
        <>
          {playlistNotices.length > 0 && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 mb-3 text-xs text-blue-900"
                 data-testid="bulk-paste-playlist-notice">
              <div className="font-semibold flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5" /> Playlist expansion
              </div>
              <ul className="list-disc ml-5 mt-1">
                {playlistNotices.map((n, i) => (
                  <li key={i}>
                    {n.playlist_id ? (
                      <><code className="font-mono text-[10px]">{n.playlist_id}</code> → <strong>{n.expanded_count}</strong> videos</>
                    ) : (
                      <span className="text-rose-600">{n.skipped || "skipped"}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {invalidPreviews.length > 0 && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 mb-3 text-xs text-rose-700">
              <strong>{invalidPreviews.length} unsupported URL(s)</strong> skipped:
              <ul className="list-disc ml-5 mt-1">
                {invalidPreviews.map((p, i) => <li key={i} className="font-mono">{p.url}</li>)}
              </ul>
            </div>
          )}
          <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="bulk-paste-rows">
            {rows.map((r, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center p-2 rounded border border-slate-100 bg-slate-50">
                {r.thumbnail_url ? <img src={r.thumbnail_url} alt="" className="col-span-1 h-10 w-16 rounded object-cover" /> : <div className="col-span-1 h-10 w-16 rounded bg-slate-200" />}
                <Input className="col-span-4" placeholder="Title" value={r.title} onChange={(e) => updateRow(idx, "title", e.target.value)} data-testid={`bulk-row-${idx}-title`} />
                <Input className="col-span-2" placeholder="mm:ss" value={r.duration} onChange={(e) => updateRow(idx, "duration", e.target.value)} data-testid={`bulk-row-${idx}-duration`} />
                <Input className="col-span-3" placeholder="tags, comma-sep" value={r.tags} onChange={(e) => updateRow(idx, "tags", e.target.value)} />
                <Badge variant="secondary" className="col-span-1 justify-center">{r.platform}</Badge>
                <button onClick={() => setRows((rs) => rs.filter((_, i) => i !== idx))}
                        className="col-span-1 text-slate-400 hover:text-rose-500 flex justify-center">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-3">
            <Button variant="ghost" onClick={() => { setPreviews(null); setRows([]); }}>Start over</Button>
            <Button onClick={saveAll} disabled={saving || rows.length === 0} className="bg-[#0071E3] hover:bg-[#0062C9] text-white" data-testid="bulk-paste-save">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save {rows.length} videos
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 *  Library Table
 * ─────────────────────────────────────────────────────────────────── */
function VideoLibraryTable({ videos, onEdit, onDelete }) {
  if (!videos.length) {
    return <div className="text-center py-12 text-sm text-slate-400" data-testid="video-library-empty">No videos in the library yet. Click <strong>+ Add video</strong> above.</div>;
  }
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid="video-library-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
          <tr>
            <th className="text-left px-3 py-2">Video</th>
            <th className="text-left px-3 py-2">Title</th>
            <th className="text-left px-3 py-2">Platform</th>
            <th className="text-left px-3 py-2">Duration</th>
            <th className="text-left px-3 py-2">Tags</th>
            <th className="text-left px-3 py-2">Used in</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {videos.map((v) => (
            <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`video-row-${v.id}`}>
              <td className="px-3 py-2">
                {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="h-8 w-14 rounded object-cover" /> : <div className="h-8 w-14 rounded bg-slate-200 flex items-center justify-center"><Film className="h-3 w-3 text-slate-400" /></div>}
              </td>
              <td className="px-3 py-2">
                <div className="text-[13px] font-semibold text-[#1B365D] line-clamp-1">{v.title}</div>
                {v.description && <div className="text-[11px] text-slate-500 line-clamp-1">{v.description}</div>}
              </td>
              <td className="px-3 py-2">
                <Badge variant="secondary" className="text-[10px]">
                  {v.platform === "youtube" ? <Youtube className="h-3 w-3 mr-1" /> : <Film className="h-3 w-3 mr-1" />}
                  {v.platform}
                </Badge>
              </td>
              <td className="px-3 py-2 text-[12px] font-mono">{v.duration}</td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {(v.tags || []).slice(0, 3).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                  {(v.tags?.length || 0) > 3 && <span className="text-[10px] text-slate-400">+{v.tags.length - 3}</span>}
                </div>
              </td>
              <td className="px-3 py-2 text-[12px] text-slate-500">{v.assignment_count} chapter{v.assignment_count === 1 ? "" : "s"}</td>
              <td className="px-3 py-2">
                <Badge variant={v.status === "active" ? "default" : "secondary"} className="text-[10px]">{v.status}</Badge>
              </td>
              <td className="px-3 py-2 text-right">
                <button onClick={() => onEdit(v)} className="text-slate-400 hover:text-[#0071E3] mr-2" data-testid={`video-edit-${v.id}`}><Edit2 className="h-4 w-4" /></button>
                <button onClick={() => onDelete(v)} className="text-slate-400 hover:text-rose-500" data-testid={`video-delete-${v.id}`}><Trash2 className="h-4 w-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 *  Edit Video modal
 * ─────────────────────────────────────────────────────────────────── */
function EditVideoModal({ video, onClose, onSaved, token }) {
  const [title, setTitle] = useState(video.title);
  const [description, setDescription] = useState(video.description || "");
  const [duration, setDuration] = useState(video.duration);
  const [tags, setTags] = useState((video.tags || []).join(", "));
  const [status, setStatus] = useState(video.status);
  // P3 — Transcript editor.  Each row: { t_sec (int), text (str) }.
  // Persisted as `transcript: [{t_sec, text}]` on the video document
  // and consumed by `TranscriptTab` for click-to-seek playback.
  const [transcript, setTranscript] = useState(() => (video.transcript || []).map((c) => ({
    t_sec: c.t_sec, text: c.text,
  })));
  const [saving, setSaving] = useState(false);

  const addCue = () => {
    const last = transcript[transcript.length - 1];
    const nextSec = last ? Math.max(0, (last.t_sec || 0) + 5) : 0;
    setTranscript((cs) => [...cs, { t_sec: nextSec, text: "" }]);
  };
  const removeCue = (idx) => setTranscript((cs) => cs.filter((_, i) => i !== idx));
  const updateCue = (idx, key, val) => setTranscript((cs) =>
    cs.map((c, i) => i === idx ? { ...c, [key]: key === "t_sec" ? (parseInt(val, 10) || 0) : val } : c));

  const submit = async () => {
    setSaving(true);
    try {
      const cleanTranscript = transcript
        .filter((c) => (c.text || "").trim())
        .sort((a, b) => (a.t_sec || 0) - (b.t_sec || 0))
        .map((c) => ({ t_sec: Math.max(0, parseInt(c.t_sec, 10) || 0), text: c.text.trim() }));
      await axios.patch(`${API}/learning-videos/videos/${video.id}`, {
        title, description, duration, status,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        transcript: cleanTranscript.length ? cleanTranscript : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Video updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-video-modal">
      <div className="bg-white rounded-2xl p-6 max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-[#1B365D]">Edit video</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="edit-video-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Duration</label>
              <Input value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="edit-video-duration" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                      className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="edit-video-status">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Tags</label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="comma-separated" data-testid="edit-video-tags" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Description</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="edit-video-description" />
          </div>
          {/* P3 — Transcript authoring */}
          <div data-testid="edit-video-transcript-editor">
            <div className="flex items-center justify-between mb-2 mt-2">
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Transcript ({transcript.length} cue{transcript.length === 1 ? "" : "s"})</label>
              <button onClick={addCue} className="text-[11px] text-[#0071E3] hover:underline flex items-center gap-1" data-testid="transcript-add-cue">
                <Plus className="h-3 w-3" /> Add cue
              </button>
            </div>
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
              {transcript.length === 0 && (
                <div className="text-[11px] text-slate-400 italic">No cues yet. Click-to-seek requires at least one cue with a timestamp.</div>
              )}
              {transcript.map((c, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`transcript-cue-${idx}`}>
                  <Input type="number" min={0} value={c.t_sec} onChange={(e) => updateCue(idx, "t_sec", e.target.value)}
                         placeholder="sec" className="col-span-2 font-mono text-xs" data-testid={`transcript-cue-${idx}-time`} />
                  <Input value={c.text} onChange={(e) => updateCue(idx, "text", e.target.value)}
                         placeholder="Cue text…" className="col-span-9 text-xs" data-testid={`transcript-cue-${idx}-text`} />
                  <button onClick={() => removeCue(idx)} className="col-span-1 text-slate-400 hover:text-rose-500 flex justify-center"
                          data-testid={`transcript-cue-${idx}-remove`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0071E3] hover:bg-[#0062C9] text-white" data-testid="edit-video-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 *  Chapter Assignment Panel
 * ─────────────────────────────────────────────────────────────────── */
function ChapterAssignmentPanel({ courses, videos, token, onEditVideo }) {
  const [selectedCourseId, setSelectedCourseId] = useState(courses[0]?.id || "");
  const [chapterVideos, setChapterVideos] = useState({});
  const [loading, setLoading] = useState(false);
  const [pickerForChapter, setPickerForChapter] = useState(null);

  const selectedCourse = courses.find((c) => c.id === selectedCourseId);

  const loadChapterVideos = useCallback(async () => {
    if (!selectedCourse) return;
    setLoading(true);
    try {
      const out = {};
      for (const ch of selectedCourse.chapters || []) {
        const res = await axios.get(`${API}/learning-videos/chapters/${selectedCourseId}/${ch.id}/videos`,
          { headers: { Authorization: `Bearer ${token}` } });
        out[ch.id] = res.data;
      }
      setChapterVideos(out);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load chapter assignments");
    } finally { setLoading(false); }
  }, [selectedCourse, selectedCourseId, token]);

  useEffect(() => { loadChapterVideos(); }, [loadChapterVideos]);

  // P3.5 — Replace a video on an existing assignment row.  Preserves
  // order via the backend `/replace` endpoint so the swapped video lands
  // in the same slot in the chapter playlist.
  const [replaceTarget, setReplaceTarget] = useState(null); // {chapterId, assignmentId}
  const doReplace = async (videoId) => {
    if (!replaceTarget) return;
    const { chapterId, assignmentId } = replaceTarget;
    try {
      await axios.patch(`${API}/learning-videos/chapters/${selectedCourseId}/${chapterId}/videos/${assignmentId}/replace`,
        { new_video_id: videoId },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Video replaced");
      setReplaceTarget(null);
      loadChapterVideos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Replace failed");
    }
  };

  const assign = async (chapterId, videoId) => {
    try {
      await axios.post(`${API}/learning-videos/chapters/${selectedCourseId}/${chapterId}/videos`,
        { video_id: videoId },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Video assigned");
      setPickerForChapter(null);
      loadChapterVideos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    }
  };

  const unassign = async (chapterId, assignmentId) => {
    try {
      await axios.delete(`${API}/learning-videos/chapters/${selectedCourseId}/${chapterId}/videos/${assignmentId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Video removed from chapter");
      loadChapterVideos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unassign failed");
    }
  };

  const setLayoutMode = async (chapterId, mode) => {
    try {
      await axios.patch(`${API}/learning-videos/chapters/${selectedCourseId}/${chapterId}/layout-mode`,
        { layout_mode: mode },
        { headers: { Authorization: `Bearer ${token}` } });
      loadChapterVideos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Layout change failed");
    }
  };

  // P3 — Drag-to-reorder.  Lightweight HTML5 DnD (no library).  Persists
  // via PATCH /reorder once the drop lands; UI optimistically updates the
  // local order first so the user sees instant feedback.
  const [dragState, setDragState] = useState(null); // {chapterId, fromIdx}

  const onDragStart = (chapterId, fromIdx) => (e) => {
    setDragState({ chapterId, fromIdx });
    try { e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
  };
  const onDragOver = (e) => { e.preventDefault(); try { e.dataTransfer.dropEffect = "move"; } catch { /* ignore */ } };
  const onDrop = (chapterId, toIdx) => async (e) => {
    e.preventDefault();
    if (!dragState || dragState.chapterId !== chapterId || dragState.fromIdx === toIdx) {
      setDragState(null); return;
    }
    const data = chapterVideos[chapterId];
    if (!data?.videos) { setDragState(null); return; }
    const next = [...data.videos];
    const [moved] = next.splice(dragState.fromIdx, 1);
    next.splice(toIdx, 0, moved);
    // Optimistic local update
    setChapterVideos((cv) => ({ ...cv, [chapterId]: { ...data, videos: next } }));
    setDragState(null);
    try {
      await axios.patch(`${API}/learning-videos/chapters/${selectedCourseId}/${chapterId}/videos/reorder`,
        { ordered_video_ids: next.map((a) => a.video.id) },
        { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reorder failed — reverting");
      loadChapterVideos();
    }
  };

  return (
    <div data-testid="chapter-assignment-panel">
      <div className="flex items-center gap-3 mb-4">
        <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Course:</label>
        <select value={selectedCourseId} onChange={(e) => setSelectedCourseId(e.target.value)}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-sm" data-testid="assignment-course-select">
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>

      {selectedCourse && (
        <div className="space-y-3">
          {(selectedCourse.chapters || []).map((ch) => {
            const data = chapterVideos[ch.id] || { videos: [], layout_mode: "stacked" };
            return (
              <div key={ch.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`chapter-row-${ch.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{ch.id}</span>
                    <h4 className="text-sm font-semibold text-[#1B365D]">{ch.title}</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-500">Layout:</span>
                    <button onClick={() => setLayoutMode(ch.id, "stacked")}
                            className={`text-[11px] px-2 py-1 rounded ${data.layout_mode === "stacked" ? "bg-[#0071E3] text-white" : "bg-slate-100 text-slate-600"}`}
                            data-testid={`layout-stacked-${ch.id}`}>Stacked</button>
                    <button onClick={() => setLayoutMode(ch.id, "carousel")}
                            className={`text-[11px] px-2 py-1 rounded ${data.layout_mode === "carousel" ? "bg-[#0071E3] text-white" : "bg-slate-100 text-slate-600"}`}
                            data-testid={`layout-carousel-${ch.id}`}>Carousel</button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {(data.videos || []).map((a, vIdx) => (
                    <div key={a.assignment_id}
                         draggable
                         onDragStart={onDragStart(ch.id, vIdx)}
                         onDragOver={onDragOver}
                         onDrop={onDrop(ch.id, vIdx)}
                         className={`flex items-center gap-3 p-2 rounded bg-slate-50 cursor-grab active:cursor-grabbing transition ${dragState?.chapterId === ch.id && dragState?.fromIdx === vIdx ? "opacity-40" : ""}`}
                         data-testid={`assignment-row-${a.assignment_id}`}>
                      <GripVertical className="h-4 w-4 text-slate-300 flex-shrink-0" />
                      {a.video.thumbnail_url ? <img src={a.video.thumbnail_url} alt="" className="h-8 w-14 rounded object-cover" /> : <div className="h-8 w-14 rounded bg-slate-200" />}
                      <div className="flex-1 text-xs min-w-0">
                        <div className="font-semibold text-[#1B365D] truncate">{a.video.title}</div>
                        <div className="text-slate-500 font-mono text-[10px]">{a.video.duration} · {a.video.platform}</div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setReplaceTarget({ chapterId: ch.id, assignmentId: a.assignment_id }); }}
                              className="text-[10px] font-semibold text-[#0071E3] hover:underline px-1.5 py-1 rounded hover:bg-blue-50"
                              data-testid={`replace-${a.assignment_id}`}>
                        Replace
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); if (onEditVideo) onEditVideo(a.video); }}
                              className="text-slate-400 hover:text-[#0071E3]"
                              title="Edit video metadata"
                              data-testid={`edit-assigned-${a.assignment_id}`}>
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => unassign(ch.id, a.assignment_id)} className="text-slate-400 hover:text-rose-500"
                              data-testid={`unassign-${a.assignment_id}`}><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
                <button onClick={() => setPickerForChapter(ch.id)}
                        className="mt-2 text-[11px] text-[#0071E3] hover:underline flex items-center gap-1"
                        data-testid={`add-to-chapter-${ch.id}`}>
                  <Plus className="h-3.5 w-3.5" /> Add video from library
                </button>
              </div>
            );
          })}
        </div>
      )}

      {(pickerForChapter || replaceTarget) && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end"
             onClick={() => { setPickerForChapter(null); setReplaceTarget(null); }}
             data-testid="video-picker-slideout">
          <div className="bg-white h-full w-[460px] shadow-2xl p-5 overflow-y-auto animate-in slide-in-from-right duration-200"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-[#1B365D]">
                  {replaceTarget ? "Replace video" : "Add video from library"}
                </h3>
                <p className="text-[11px] text-slate-500">
                  {replaceTarget ? "Pick a replacement — order is preserved" : "Pick one to assign to this chapter"}
                </p>
              </div>
              <button onClick={() => { setPickerForChapter(null); setReplaceTarget(null); }}
                      className="text-slate-400 hover:text-slate-700" data-testid="picker-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-1.5">
              {videos.filter((v) => v.status === "active").map((v) => (
                <button key={v.id}
                        onClick={() => replaceTarget ? doReplace(v.id) : assign(pickerForChapter, v.id)}
                        className="w-full flex items-center gap-3 p-2 rounded border border-slate-100 hover:bg-slate-50 hover:border-[#0071E3]/30 text-left transition"
                        data-testid={`picker-video-${v.id}`}>
                  {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="h-10 w-16 rounded object-cover" /> : <div className="h-10 w-16 rounded bg-slate-200" />}
                  <div className="text-xs flex-1 min-w-0">
                    <div className="font-semibold text-[#1B365D] truncate">{v.title}</div>
                    <div className="text-slate-500 font-mono text-[10px]">{v.duration} · {v.platform}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 *  Main panel
 * ─────────────────────────────────────────────────────────────────── */
export default function VideoLibraryPanel({ courses }) {
  const { token } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("library");
  const [search, setSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (platformFilter !== "all") params.platform = platformFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await axios.get(`${API}/learning-videos/videos`, {
        headers: { Authorization: `Bearer ${token}` }, params,
      });
      setVideos(res.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load videos");
    } finally { setLoading(false); }
  }, [token, search, platformFilter, statusFilter]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleDelete = async (v) => {
    if (!window.confirm(`Delete "${v.title}"? It will be removed from ${v.assignment_count} chapter${v.assignment_count === 1 ? "" : "s"}.`)) return;
    try {
      await axios.delete(`${API}/learning-videos/videos/${v.id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Deleted (also unassigned from ${v.assignment_count} chapters)`);
      fetchVideos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="video-library-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-[#1B365D]">Learning Paths — Video Library</h2>
          <p className="text-xs text-slate-500">Paste video URLs to build a centralized library. Assign videos to chapters below.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setAdding(!adding); setBulk(false); }} className="bg-[#0071E3] hover:bg-[#0062C9] text-white" data-testid="open-add-video">
            <Plus className="h-4 w-4 mr-1" /> Add video
          </Button>
          <Button variant="outline" onClick={() => { setBulk(!bulk); setAdding(false); }} data-testid="open-bulk-paste">
            <Layers className="h-4 w-4 mr-1" /> Bulk paste
          </Button>
        </div>
      </div>

      {adding && <AddVideoForm onSaved={() => { setAdding(false); fetchVideos(); }} onCancel={() => setAdding(false)} token={token} />}
      {bulk && <BulkPasteForm onSaved={() => { setBulk(false); fetchVideos(); }} onCancel={() => setBulk(false)} token={token} />}

      <div className="flex gap-2 mb-3 border-b border-slate-200">
        <button onClick={() => setTab("library")}
                className={`text-sm font-semibold pb-2 px-1 -mb-px ${tab === "library" ? "text-[#0071E3] border-b-2 border-[#0071E3]" : "text-slate-500"}`}
                data-testid="video-tab-library">Video Library</button>
        <button onClick={() => setTab("assign")}
                className={`text-sm font-semibold pb-2 px-1 -mb-px ${tab === "assign" ? "text-[#0071E3] border-b-2 border-[#0071E3]" : "text-slate-500"}`}
                data-testid="video-tab-assign">Chapter Assignment</button>
      </div>

      {tab === "library" && (
        <>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <Input className="pl-8" placeholder="Search title or tag" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="video-library-search" />
            </div>
            <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}
                    className="rounded-md border border-slate-200 px-3 text-sm" data-testid="video-library-platform">
              <option value="all">All platforms</option>
              <option value="youtube">YouTube</option>
              <option value="vimeo">Vimeo</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="rounded-md border border-slate-200 px-3 text-sm" data-testid="video-library-status">
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          {loading ? <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#0071E3] mx-auto" /></div>
                   : <VideoLibraryTable videos={videos} onEdit={setEditing} onDelete={handleDelete} />}
        </>
      )}

      {tab === "assign" && (
        <ChapterAssignmentPanel courses={courses} videos={videos} token={token} onEditVideo={setEditing} />
      )}

      {editing && <EditVideoModal video={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchVideos(); }} token={token} />}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 *  Transcript tab — click-to-seek cue list (P3)
 *
 *  Fires a `learning-video:seek` window event with `{ videoId, t_sec }`.
 *  `VideoCustomControls` listens and calls `seekTo` on the active player.
 *  We also auto-highlight the active cue based on current player position
 *  (received via the `currentPos` prop polled by the parent strip).
 * ─────────────────────────────────────────────────────────────────── */
function fmtCue(t) {
  if (!Number.isFinite(t) || t < 0) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function TranscriptTab({ video, currentPos }) {
  const cues = video.transcript || [];
  // Scroll-into-view discipline: only scroll when the user isn't
  // actively interacting with the transcript (avoids yanking their
  // scroll while they're reading a different cue). We re-arm the
  // userScrolling lock 1.5s after the last manual scroll.
  const containerRef = useRef(null);
  const cueRefs = useRef([]);
  const userScrolledAtRef = useRef(0);
  const onUserScroll = useCallback(() => { userScrolledAtRef.current = Date.now(); }, []);

  if (!cues.length) {
    return (
      <div className="px-4 py-6 text-center text-xs text-slate-400" data-testid="transcript-tab-empty">
        No transcript available for this video.
      </div>
    );
  }
  // Active cue = last cue whose t_sec ≤ currentPos
  let activeIdx = -1;
  for (let i = 0; i < cues.length; i += 1) {
    if (cues[i].t_sec <= (currentPos || 0)) activeIdx = i;
    else break;
  }
  return (
    <div
      ref={containerRef}
      onWheel={onUserScroll}
      onTouchMove={onUserScroll}
      className="max-h-[320px] overflow-y-auto px-2 py-2 space-y-0.5"
      data-testid="transcript-tab">
      {cues.map((c, idx) => (
        <button
          key={idx}
          ref={(el) => { cueRefs.current[idx] = el; }}
          onClick={() => window.dispatchEvent(new CustomEvent("learning-video:seek",
            { detail: { videoId: video.video_id, t_sec: c.t_sec } }))}
          className={`w-full text-left px-3 py-2 rounded-lg text-xs flex gap-3 transition ${idx === activeIdx ? "bg-[#0071E3]/10 text-[#1B365D]" : "hover:bg-slate-50 text-slate-700"}`}
          data-testid={`transcript-cue-${idx}`}>
          <span className={`font-mono text-[10px] flex-shrink-0 mt-0.5 ${idx === activeIdx ? "text-[#0071E3] font-bold" : "text-slate-400"}`}>
            {fmtCue(c.t_sec)}
          </span>
          <span className="flex-1">{c.text}</span>
        </button>
      ))}
      <_AutoScrollCue
        containerRef={containerRef}
        cueRefs={cueRefs}
        activeIdx={activeIdx}
        userScrolledAtRef={userScrolledAtRef} />
    </div>
  );
}

/* Side-effect-only helper: when the active cue changes, scroll the
 * cue into the container's view (centered). Honors a 1.5s "user is
 * scrolling" lock so we don't fight the learner. Pulled out so the
 * effect cleanup doesn't muddy the JSX above. */
function _AutoScrollCue({ containerRef, cueRefs, activeIdx, userScrolledAtRef }) {
  useEffect(() => {
    if (activeIdx < 0) return;
    const recentlyScrolled = Date.now() - userScrolledAtRef.current < 1500;
    if (recentlyScrolled) return;
    const el = cueRefs.current[activeIdx];
    const container = containerRef.current;
    if (!el || !container) return;
    // Only scroll if the cue is off-screen within the container.
    const elRect = el.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    const above = elRect.top < cRect.top + 8;
    const below = elRect.bottom > cRect.bottom - 8;
    if (!above && !below) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [activeIdx, containerRef, cueRefs, userScrolledAtRef]);
  return null;
}

/* ─────────────────────────────────────────────────────────────────────
 *  Chapter-reader video strip — renders at top of ChapterReader for any
 *  chapter that has videos assigned.
 *  P1: basic iframe.
 *  P2: custom YT controls bar (play/pause, scrub, speed, captions, fullscreen).
 *  P3: Transcript tab w/ click-to-seek + auto-mark-complete at ≥90% watch.
 * ─────────────────────────────────────────────────────────────────── */
export function VideoPlayerStrip({ courseId, chapterId, accent = "#0071E3", onChapterAutoComplete, onActiveVideoChange, onProgressTick }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  // Track whether we've already fired the 90% auto-mark-complete for
  // this chapter open so we don't spam the API on every poll tick.
  const [autoCompleteFired, setAutoCompleteFired] = useState(false);

  useEffect(() => {
    if (!courseId || !chapterId) return;
    setData(null); setActiveIdx(0); setAutoCompleteFired(false);
    axios.get(`${API}/learning-videos/chapters/${courseId}/${chapterId}/videos`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData({ videos: [], layout_mode: "stacked" }));
  }, [courseId, chapterId, token]);

  // P3 — Auto-mark-complete at ≥90% watch + surface progress + active
  // video up to the parent (so the chapter page can render the transcript
  // tab next to Notes / Resources instead of inside the player itself).
  const handleProgress = useCallback(({ pct, pos, dur, playing }) => {
    if (onProgressTick) { try { onProgressTick({ pos, dur, pct, playing }); } catch { /* ignore */ } }
    if (!autoCompleteFired && pct >= 0.9 && onChapterAutoComplete) {
      setAutoCompleteFired(true);
      try { onChapterAutoComplete(); } catch { /* ignore */ }
    }
  }, [autoCompleteFired, onChapterAutoComplete, onProgressTick]);

  // Tell the parent which video is active whenever it changes (initial
  // load + carousel click).
  useEffect(() => {
    if (!data || !data.videos?.length || !onActiveVideoChange) return;
    try { onActiveVideoChange(data.videos[activeIdx]?.video || null); } catch { /* ignore */ }
  }, [data, activeIdx, onActiveVideoChange]);

  if (!data || !data.videos?.length) {
    // Empty-state placeholder so the chapter page still has visual
    // anchor at the top of the right column.
    return (
      <div className="aspect-video w-full bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400 text-sm mb-5"
           data-testid="video-player-strip-empty">
        No video assigned to this chapter
      </div>
    );
  }
  const active = data.videos[activeIdx];
  const useCustom = active.video.platform === "youtube" && isCustomControlsEnabled();

  return (
    <div className="mb-5 rounded-2xl overflow-hidden bg-black" data-testid="video-player-strip">
      {useCustom ? (
        <VideoCustomControls
          key={active.video.id}
          videoId={active.video.video_id}
          title={active.video.title}
          onProgress={handleProgress}
        />
      ) : (
        <div className="aspect-video w-full bg-black">
          <iframe
            key={active.video.id}
            src={active.video.embed_url}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            title={active.video.title}
            data-testid="video-player-iframe"
          />
        </div>
      )}

      {/* Auto-complete pill — keeps the visual feedback even after we
          lifted the transcript out of the player. */}
      {autoCompleteFired && (
        <div className="bg-slate-800 px-3 py-1.5 text-right" data-testid="auto-complete-fired">
          <span className="text-[10px] text-emerald-400 font-semibold uppercase tracking-wide">
            ✓ Auto-completed at 90%
          </span>
        </div>
      )}

      {data.videos.length > 1 && (
        <div className={`bg-slate-900 px-4 py-3 flex gap-2 ${data.layout_mode === "carousel" ? "overflow-x-auto" : "flex-col"}`}
             data-testid={`video-strip-${data.layout_mode}`}>
          {data.videos.map((a, idx) => (
            <button key={a.assignment_id} onClick={() => setActiveIdx(idx)}
                    className={`flex items-center gap-3 p-2 rounded text-left transition ${idx === activeIdx ? "bg-white/15 border-l-2" : "bg-white/5 hover:bg-white/10"} ${data.layout_mode === "carousel" ? "flex-shrink-0 min-w-[260px]" : ""}`}
                    style={idx === activeIdx ? { borderLeftColor: accent } : {}}
                    data-testid={`video-strip-item-${idx}`}>
              {a.video.thumbnail_url ? <img src={a.video.thumbnail_url} alt="" className="h-10 w-16 rounded object-cover" /> : <div className="h-10 w-16 rounded bg-slate-700" />}
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-semibold text-white line-clamp-1">{a.video.title}</div>
                <div className="text-[10px] text-slate-400 font-mono">{a.video.duration}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
